import { Alert, Form, message, Modal, Select } from 'antd'
import { useState } from 'react'

interface Option {
  label: string
  value: string
}

interface Props {
  open: boolean
  title: string
  label: string
  fieldName: string
  userIds: string[]
  userEmail?: string
  currentValue?: string
  options: Option[]
  optionsLoading?: boolean
  optionsError?: string
  showSearch?: boolean
  onSubmit: (value: string, userIds: string[]) => Promise<void>
  onClose: () => void
}

export function SetPropertyModal({
  open,
  title,
  label,
  fieldName,
  userIds,
  userEmail,
  currentValue,
  options,
  optionsLoading,
  optionsError,
  showSearch,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)
      await onSubmit(values[fieldName] as string, userIds)
      form.resetFields()
      onClose()
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  const target =
    userIds.length > 1 ? `${userIds.length} users` : (userEmail ?? userIds[0])

  return (
    <Modal
      title={`${title} · ${target}`}
      open={open}
      onOk={handleSubmit}
      okText={title}
      onCancel={onClose}
      confirmLoading={loading}
      okButtonProps={{ disabled: Boolean(optionsError) }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ [fieldName]: currentValue }}
      >
        {optionsError && (
          <Alert
            type="error"
            showIcon
            message={optionsError}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form.Item
          name={fieldName}
          label={label}
          rules={[
            {
              required: true,
              message: `Please select a ${label.toLowerCase()}`,
            },
          ]}
        >
          <Select
            placeholder={`Select ${label.toLowerCase()}`}
            showSearch={showSearch}
            options={options}
            loading={optionsLoading}
            disabled={Boolean(optionsError)}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
